import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import withAuth from '../../auth';
import { Breadcrumb } from 'react-bootstrap';
import moment from 'moment';
import { AuthContext } from '../AuthContext';
import Spinner from 'react-bootstrap/Spinner';
import NPMapViewDirections from '../google_map/NPMapViewDirections';
import { GoogleMapsProvider } from '../google_map/GoogleMapsContext';
import { formatPhoneNumberWithMask } from '../../formatPhoneNumber';
import { handleApiRequest } from '../../utils/apiHandler';
import api from '../../services/axios';
import { renderStars } from '../../utils/renderStars';
import { PICKUP_STATUS_COLORS } from '../../constants/colors';
import { usePickupUpdateEvent } from '../../hooks/usePickupRealtime';

const PickupDetails = () => {
  const { pickupId } = useParams();
  const [pickup, setPickup] = useState(null);
  const [trackers, setTrackers] = useState(null);

  const { hasPermission } = useContext(AuthContext);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [isBookSubmitted, setIsBookSubmitted] = useState(false);

  const [isUnclaimSubmitted, setIsUnclaimSubmitted] = useState(false);
  const [isCancelSubmitted, setIsCancelSubmitted] = useState(false);

  const [claimDriverId, setClaimDriverId] = useState(0);
  const [isClaimSubmitted, setIsClaimSubmitted] = useState(false);

  const [isMarkPaidSubmitted, setIsMarkPaidSubmitted] = useState(false);
  const [paidMethod, setPaidMethod] = useState('EFT');
  const [paidReference, setPaidReference] = useState('');

  const fetchPickup = useCallback(async () => {
    await handleApiRequest({
      requestFn: () => api.get(`/manager/pickups/${pickupId}`),
      onSuccess: (res) => {
        setPickup(res.data);
        setTrackers(res.trackers);
      },
      onError: (error) => {
        console.error('Error fetching the delivery details!', error);
      },
    });
  }, [pickupId]);

  useEffect(() => {
    fetchPickup();
  }, [fetchPickup]);

  usePickupUpdateEvent({
    channelId: pickup?.channel_id,
    onUpdated: (event) => {
      if (String(event.pickup.id) !== String(pickupId)) return;

      setPickup((currentPickup) => ({
        ...currentPickup,
        ...event.pickup,
      }));

      if (event.trackers !== undefined) {
        setTrackers(event.trackers);
      }

      // Secured and completion photos are nested under item locations. Fetch
      // the canonical details payload so those URLs cannot remain stale.
      fetchPickup();
    },
    onReconnect: fetchPickup,
  });

  useEffect(() => {
    if (!success && !error) return;

    const timer = setTimeout(() => {
      setSuccess('');
      setError('');
    }, 2000);

    return () => clearTimeout(timer);
  }, [success, error]);

  const bookPickupOnBehalf = async () => {
    if (
      !window.confirm(
        'Are you sure that you want to book this delivery on behalf of the customer with invoice payment?',
      )
    ) {
      return false;
    }

    setIsBookSubmitted(true);

    await handleApiRequest({
      requestFn: () =>
        api.post(`/manager/pickups/${pickup.id}/waiting-for-driver`, {
          payment_type: 'invoice',
        }),
      onSuccess: (res) => {
        setSuccess(res.message || 'Delivery booked successfully');
        if (res.pickup) {
          setPickup(res.pickup);
        } else {
          setPickup((prev) => ({
            ...prev,
            payment_type: 'invoice',
            waiting_for_driver_at: new Date().toISOString(),
          }));
        }
      },
      onError: (error) => {
        setError(error);
      },
      finallyFn: () => {
        setIsBookSubmitted(false);
      },
    });
  };

  const claimPickup = async () => {
    if (
      !window.confirm(
        'Are you sure that you want to claim this delivery on behalf of the driver?',
      )
    )
      return false;

    setIsClaimSubmitted(true);
    await handleApiRequest({
      requestFn: () =>
        api.post(`/manager/pickups/${pickup.id}/claim`, {
          driver_id: claimDriverId,
        }),
      onSuccess: (res) => {
        setSuccess(res.message);
        setPickup(res.pickup);
      },
      onError: (error) => {
        setError(error);
      },
      finallyFn: () => {
        setIsClaimSubmitted(false);
      },
    });
  };

  const unclaimPickup = async () => {
    if (
      !window.confirm(
        'Are you sure that you want to unclaim this delivery from the current driver?',
      )
    )
      return false;

    setIsUnclaimSubmitted(true);

    await handleApiRequest({
      requestFn: () => api.post(`/manager/pickups/${pickup.id}/unclaim`, {}),
      onSuccess: (res) => {
        setSuccess(res.message);
        setPickup(res.pickup);
      },
      onError: (error) => {
        setError(error);
      },
      finallyFn: () => {
        setIsUnclaimSubmitted(false);
      },
    });
  };

  const cancelPickup = async () => {
    if (!window.confirm('Are you sure that you want to cancel this delivery?'))
      return false;

    setIsCancelSubmitted(true);
    await handleApiRequest({
      requestFn: () => api.post(`/manager/pickups/${pickup.id}/cancel`, {}),
      onSuccess: (res) => {
        setSuccess(res.message);
        setPickup(res.pickup);
      },
      onError: (error) => {
        setError(error);
      },
      finallyFn: () => {
        setIsCancelSubmitted(false);
      },
    });
  };

  const downloadInvoice = async () => {
    await handleApiRequest({
      requestFn: () => api.get(`/manager/pickups/${pickup.id}/invoice/pdf`),
      onSuccess: (res) => {
        const data = res;
        if (data?.invoice_pdf || data?.url) {
          window.open(data.invoice_pdf || data.url, '_blank');
        } else {
          alert('Invoice not found for this delivery.');
        }
      },
      onError: () => {
        alert('Failed to fetch the invoice. Please try again later.');
      },
    });
  };

  const downloadReceipt = async () => {
    await handleApiRequest({
      requestFn: () => api.get(`/manager/pickups/${pickup.id}/receipt/pdf`),
      onSuccess: (res) => {
        const data = res;
        if (data?.url) {
          window.open(data.url, '_blank');
        } else {
          alert('Receipt not found for this delivery.');
        }
      },
      onError: () => {
        alert('Failed to fetch the receipt. Please try again later.');
      },
    });
  };

  const markInvoicePaid = async () => {
    if (!window.confirm('Mark this invoice as paid?')) return false;

    setIsMarkPaidSubmitted(true);

    await handleApiRequest({
      requestFn: () =>
        api.post(`/manager/pickups/${pickup.id}/invoice/mark-paid`, {
          paid_method: paidMethod,
          paid_reference: paidReference || null,
        }),
      onSuccess: (res) => {
        setSuccess(res.message || 'Invoice marked as paid');

        setPickup((prev) => ({
          ...prev,
          invoice_status: 'paid',
          payment_status_id: 1,
        }));
      },
      onError: (error) => {
        setError(error);
      },
      finallyFn: () => {
        setIsMarkPaidSubmitted(false);
      },
    });
  };

  return (
    <div className="container-fluid mt-3">
      <Breadcrumb>
        <Breadcrumb.Item href="/pickups">Deliveries</Breadcrumb.Item>
        <Breadcrumb.Item active>Delivery Details</Breadcrumb.Item>
      </Breadcrumb>
      {pickup ? (
        <div>
          <h1 className="text-center" style={{ position: 'relative' }}>
            Delivery Details #{pickup ? pickup.id : ''}
            {pickup.user && hasPermission('user-update') && (
              <div className="duplicate-pickup-wrap">
                <Link
                  to={`/pickups/create/${pickup.user.id}?duplicate=${pickup.id}`}
                  className="duplicate-pickup-btn btn btn-outline-primary"
                >
                  Duplicate
                </Link>
              </div>
            )}
          </h1>
          {success && <div className="alert alert-success">{success}</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          <div className="row">
            <div className="col-md-6">
              <div className="card mb-2">
                <div className="card-body" align="center">
                  <h6>ID: {pickup.id}</h6>
                  <h6
                    className="text-center p-2"
                    style={{
                      backgroundColor: !pickup.delivery_status?.id
                        ? 'initial'
                        : PICKUP_STATUS_COLORS[pickup.delivery_status?.id] ||
                          'initial',
                    }}
                  >
                    Delivery Status: {pickup.delivery_status.status}
                  </h6>
                  <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                    {pickup.delivery_status?.id !== 6 && (
                      <Link
                        to={`/pickups/update/${pickup.id}`}
                        className="duplicate-pickup-btn btn btn-warning"
                      >
                        Edit Delivery Details
                      </Link>
                    )}
                    {pickup.delivery_status.id !== 5 &&
                      pickup.delivery_status.id !== 6 && (
                        <>
                          {' '}
                          <button
                            className="btn btn-danger ml-2"
                            onClick={cancelPickup}
                          >
                            {isCancelSubmitted ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                                &nbsp;Canceling...
                              </>
                            ) : (
                              'Cancel'
                            )}
                          </button>
                        </>
                      )}
                  </div>
                  <>
                    {pickup.delivery_status.id === 0 &&
                      !pickup.waiting_for_driver_at && (
                        <button
                          className="btn btn-warning mb-2"
                          onClick={bookPickupOnBehalf}
                          disabled={isBookSubmitted}
                        >
                          {isBookSubmitted ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />
                              &nbsp;Booking...
                            </>
                          ) : (
                            'Book on Behalf'
                          )}
                        </button>
                      )}
                  </>
                  <p>
                    Customer:{' '}
                    {pickup.user && (
                      <Link to={`/users/${pickup.user.id}`}>
                        <b>
                          {pickup.user.id} {pickup.user.full_name}
                        </b>{' '}
                        ({pickup.user.email})
                      </Link>
                    )}
                    <br />
                    Driver:{' '}
                    {pickup.driver && (
                      <Link to={`/drivers/${pickup.driver.id}`}>
                        <b>
                          {pickup.driver.id} {pickup.driver.name}
                        </b>{' '}
                        ({pickup.driver.email})
                      </Link>
                    )}
                  </p>
                  <p className="mb-0">
                    Truck Type: <strong>{pickup.truck_type.title}</strong>
                    {pickup.vehicle && (
                      <span>
                        <br />
                        Vehicle:{' '}
                        <Link to={`/vehicles/${pickup.vehicle.id}`}>
                          <b>
                            {pickup.vehicle.id} {pickup.vehicle.make}{' '}
                            {pickup.vehicle.model}
                          </b>
                        </Link>
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <ul className="list-group mb-2">
                {pickup.additional_info && (
                  <li className="list-group-item">
                    Additional info: <strong>{pickup.additional_info}</strong>
                  </li>
                )}
                {pickup.claim && (
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    <span>
                      Claim: <strong>{pickup.claim.status}</strong>
                    </span>
                    &nbsp;
                    {/* 1 - claimed, 5 - Complete, 6 - Cancelled*/}
                    {pickup.driver &&
                      pickup.delivery_status?.id !== 5 &&
                      pickup.delivery_status?.id !== 6 && (
                        <button
                          className="btn btn-primary"
                          onClick={unclaimPickup}
                        >
                          {isUnclaimSubmitted ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />{' '}
                              Unclaim...
                            </>
                          ) : (
                            'Unclaim'
                          )}
                        </button>
                      )}
                    {!pickup.driver &&
                      pickup.waiting_for_driver_at &&
                      pickup.delivery_status?.id !== 5 &&
                      pickup.delivery_status?.id !== 6 && (
                        <div className="d-flex align-items-center">
                          <label className="mb-0 mr-2">Driver ID:</label>
                          <input
                            type="number"
                            className="form-control text-center input-no-spinners mr-2"
                            placeholder="ID"
                            value={claimDriverId}
                            onChange={(e) => setClaimDriverId(e.target.value)}
                            style={{ maxWidth: '75px', flex: '1 1 75px' }}
                          />
                          <button
                            className="btn btn-warning"
                            onClick={claimPickup}
                          >
                            {isClaimSubmitted ? (
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />
                            ) : (
                              'Сlaim on Behalf'
                            )}
                          </button>
                        </div>
                      )}
                  </li>
                )}
                {pickup.cancelled_by_driver && (
                  <li className="list-group-item">
                    Canceled by driver:{' '}
                    <Link to={`/drivers/${pickup.cancelled_by_driver.id}`}>
                      <b>
                        {pickup.cancelled_by_driver.id}{' '}
                        {pickup.cancelled_by_driver.name}
                      </b>{' '}
                      ({pickup.cancelled_by_driver.email})
                    </Link>
                  </li>
                )}
              </ul>

              <div className="card mb-2">
                <div className="card-header">
                  <h5 className="mb-0 text-center">Prices</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive mb-4">
                    <table className="table table-sm table-striped text-center mb-0">
                      <tbody>
                        {pickup.payment_type && (
                          <tr>
                            <td>Payment Type</td>
                            <td>
                              <strong>{pickup.payment_type}</strong>
                            </td>
                          </tr>
                        )}
                        {pickup.is_promo_applied && (
                          <>
                            <tr>
                              <td>Regular Delivery Fee (Delivery Cost)</td>
                              <td className="">
                                ${pickup.regular_delivery_cost}
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <Link
                                  to={`/promo-codes/${pickup.promo_code_id}`}
                                >
                                  Promo Discount (
                                  {pickup.promo_code?.discount_type ===
                                    'percentage' && (
                                    <span>
                                      {pickup.promo_code?.discount_value}%
                                    </span>
                                  )}
                                  {pickup.promo_code?.discount_type ===
                                    'fixed' && (
                                    <span>
                                      ${pickup.promo_code?.discount_value}
                                    </span>
                                  )}
                                  )
                                </Link>
                              </td>
                              <td className="text-info">
                                <b>-${pickup.discount_amount}</b>
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          {pickup.is_promo_applied ? (
                            <td>Delivery Fee</td>
                          ) : (
                            <td>Delivery Fee (Delivery Cost)</td>
                          )}
                          <td>${pickup.delivery_cost}</td>
                        </tr>
                        <tr>
                          <td>
                            Cargo Protection Pkg (
                            {pickup.cargo_insurance.insurance_range})
                          </td>
                          <td>${pickup.insurance_cost}</td>
                        </tr>
                        <tr>
                          <td>Cost (Delivery Fee + Cargo Protection Pkg)</td>
                          <td>
                            <b>${pickup.cost}</b>
                          </td>
                        </tr>
                        <tr>
                          <td>GST {parseFloat(pickup.tax_rate?.gst || 0)}%</td>
                          <td>${pickup.gst_five_percent}</td>
                        </tr>
                        <tr>
                          <td>PST {parseFloat(pickup.tax_rate?.pst || 0)}%</td>
                          <td>${pickup.pst_seven_percent}</td>
                        </tr>
                        <tr>
                          <td>HST {parseFloat(pickup.tax_rate?.hst || 0)}%</td>
                          <td>${pickup.hst_amount}</td>
                        </tr>
                        <tr>
                          <td>QST {parseFloat(pickup.tax_rate?.qst || 0)}%</td>
                          <td>${pickup.qst_amount}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Total Cost</b>
                          </td>
                          <td>
                            <b>${pickup.total_cost}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Invoice Button */}
                  {pickup.delivery_status.id === 5 && (
                    <div className="text-center mt-4">
                      <div className="d-flex flex-column align-items-center gap-2">
                        <button
                          className="btn btn-primary mb-2"
                          onClick={downloadInvoice}
                        >
                          Download Invoice
                        </button>

                        {pickup.payment_type === 'invoice' &&
                          pickup.invoice_status !== 'paid' && (
                            <div
                              className="card p-3 mt-2"
                              style={{ maxWidth: '420px', width: '100%' }}
                            >
                              <h6 className="mb-3 text-center">
                                Mark Invoice Paid
                              </h6>

                              <div className="mb-2 text-left">
                                <label className="form-label">
                                  Payment Method
                                </label>
                                <select
                                  className="form-control"
                                  value={paidMethod}
                                  onChange={(e) =>
                                    setPaidMethod(e.target.value)
                                  }
                                >
                                  <option value="EFT">
                                    EFT (Electronic Funds Transfer)
                                  </option>
                                  <option value="Wire">Wire</option>
                                  <option value="Cash">Cash</option>
                                  <option value="Cheque">Cheque</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>

                              <div className="mb-3 text-left">
                                <label className="form-label">Reference</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Optional reference"
                                  value={paidReference}
                                  onChange={(e) =>
                                    setPaidReference(e.target.value)
                                  }
                                />
                              </div>

                              <button
                                className="btn btn-success"
                                onClick={markInvoicePaid}
                                disabled={isMarkPaidSubmitted}
                              >
                                {isMarkPaidSubmitted ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    &nbsp;Marking Paid...
                                  </>
                                ) : (
                                  'Mark as Paid'
                                )}
                              </button>
                            </div>
                          )}

                        {pickup.payment_type === 'invoice' &&
                          (pickup.invoice_status === 'paid' ||
                            pickup.payment_status_id === 1) && (
                            <button
                              className="btn btn-outline-success mt-2"
                              onClick={downloadReceipt}
                            >
                              Download Receipt
                            </button>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <GoogleMapsProvider>
                <NPMapViewDirections
                  origins={pickup.origins}
                  dropoffs={pickup.dropoffs}
                />
              </GoogleMapsProvider>
              <p className="mt-2">
                Origin: <b>{pickup.origin_address}</b>
                <br />
                Dropoff: <b>{pickup.dropoff_address}</b>
                <br />
                Distance: <b>{pickup.distance} km</b>
              </p>
              <div className="card my-2">
                <div className="card-body">
                  <h5 className="text-center">Timestamps</h5>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered text-center mb-0">
                      <tbody>
                        {pickup.scheduled_pickup_time && (
                          <tr className="table-info">
                            <td>Scheduled Delivery Time</td>
                            <td>
                              {moment(pickup.scheduled_pickup_time).format(
                                'YYYY-MM-DD HH:mm:ss',
                              )}
                            </td>
                          </tr>
                        )}
                        {pickup.created_at && (
                          <tr>
                            <td>Created At</td>
                            <td>
                              {moment(pickup.created_at).format(
                                'YYYY-MM-DD HH:mm:ss',
                              )}
                            </td>
                          </tr>
                        )}
                        {pickup.waiting_for_driver_at && (
                          <tr>
                            <td>Waiting for Driver At</td>
                            <td>
                              {moment(pickup.waiting_for_driver_at).format(
                                'YYYY-MM-DD HH:mm:ss',
                              )}
                            </td>
                          </tr>
                        )}
                        {pickup.claimed_at && (
                          <tr>
                            <td>Claimed At</td>
                            <td>
                              {moment(pickup.claimed_at).format(
                                'YYYY-MM-DD HH:mm:ss',
                              )}
                            </td>
                          </tr>
                        )}
                        {pickup.started_at && (
                          <tr>
                            <td>Started At</td>
                            <td>
                              {moment(pickup.started_at).format(
                                'YYYY-MM-DD HH:mm:ss',
                              )}
                            </td>
                          </tr>
                        )}

                        {trackers &&
                          trackers.map((tracker) => (
                            <tr key={tracker.id}>
                              <td>
                                {tracker.action_title}{' '}
                                <b>
                                  {tracker.origin_items &&
                                    tracker.origin_items.join(', ')}{' '}
                                  {tracker.dropoff_items &&
                                    tracker.dropoff_items.join(', ')}
                                </b>
                                {tracker.origin && (
                                  <>
                                    <br />
                                    <small>{tracker.origin.address}</small>
                                  </>
                                )}
                                {tracker.dropoff && (
                                  <>
                                    <br />
                                    <small>{tracker.dropoff.address}</small>
                                  </>
                                )}
                              </td>
                              <td>
                                {tracker.completed
                                  ? moment(tracker.updated_at).format(
                                      'YYYY-MM-DD HH:mm:ss',
                                    )
                                  : '---'}
                              </td>
                            </tr>
                          ))}

                        {pickup.completed_at && (
                          <tr>
                            <td>Completed At</td>
                            <td>
                              {moment(pickup.completed_at).format(
                                'YYYY-MM-DD HH:mm:ss',
                              )}
                            </td>
                          </tr>
                        )}
                        {pickup.canceled_at && (
                          <tr>
                            <td>Cancelled At</td>
                            <td>
                              {moment(pickup.canceled_at).format(
                                'YYYY-MM-DD HH:mm:ss',
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-2">
            <div className="card mb-2">
              <div className="card-header">
                <h5 className="mb-0 text-center">Items</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm table-striped text-center mb-0">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Photo</th>
                        <th>Name / Details</th>
                        <th>Weight / Size</th>
                        <th>Pickup Address</th>
                        <th>Dropoff Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pickup.items.map((item) => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td>
                            {item.photo && (
                              <a
                                href={item.photo}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img src={item.photo} alt="Item" width="50" />
                              </a>
                            )}
                          </td>
                          <td>
                            {item.item_name}
                            <br />
                            {item.details}
                          </td>
                          <td>
                            {item.weight}
                            <br />
                            {item.size}
                          </td>
                          <td>
                            {item.origin && (
                              <>
                                <b>{item.origin.address}</b>
                                <br />
                                {item.origin.contact_name && (
                                  <>
                                    {item.origin.contact_name}
                                    <br />
                                  </>
                                )}
                                {item.origin.contact_phone && (
                                  <>
                                    {formatPhoneNumberWithMask(
                                      item.origin.contact_phone || '',
                                      '+9 999 999-9999',
                                    )}
                                    <br />
                                  </>
                                )}
                                <div className="card p-2" align="center">
                                  <small>Secured Photo</small> <br />{' '}
                                  {item.origin.photo_url ? (
                                    <a
                                      href={item.origin.photo_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <img
                                        src={item.origin.photo_url}
                                        alt="Secured Item Photo"
                                        width="100"
                                      />
                                    </a>
                                  ) : (
                                    'Not Uploaded'
                                  )}
                                </div>
                              </>
                            )}
                          </td>
                          <td>
                            {item.dropoff && (
                              <>
                                <b>{item.dropoff.address}</b>
                                <br />
                                {item.dropoff.contact_name && (
                                  <>
                                    {item.dropoff.contact_name}
                                    <br />
                                  </>
                                )}
                                {item.dropoff.contact_phone && (
                                  <>
                                    {formatPhoneNumberWithMask(
                                      item.dropoff.contact_phone || '',
                                      '+9 999 999-9999',
                                    )}
                                    <br />
                                  </>
                                )}
                                <div className="card p-2" align="center">
                                  <small>Complete Photo</small> <br />{' '}
                                  {item.dropoff.photo_url ? (
                                    <a
                                      href={item.dropoff.photo_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <img
                                        src={item.dropoff.photo_url}
                                        alt="Complete Item Photo"
                                        width="100"
                                      />
                                    </a>
                                  ) : (
                                    'Not Uploaded'
                                  )}
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Feedbacks Section */}
          {pickup.feedbacks && (
            <div className="mt-4">
              <h5 className="text-center">Feedbacks</h5>
              <div className="row">
                {/* Feedback from Customer */}
                <div className="col-12 col-lg-6 mb-4">
                  <div className="card">
                    <div className="card-body">
                      <h6>
                        Feedback from Customer&nbsp;
                        {pickup.user && (
                          <Link to={`/users/${pickup.user.id}`}>
                            <b>
                              {pickup.user.id} {pickup.user.full_name}
                            </b>
                          </Link>
                        )}
                      </h6>
                      {pickup.feedbacks.user &&
                      pickup.feedbacks.user.rating_by === 1 ? (
                        <>
                          <p>
                            Rating:{' '}
                            {renderStars(
                              parseFloat(pickup.feedbacks.user.rate) || 0,
                            )}
                          </p>
                          <p>Feedback: {pickup.feedbacks.user.feedback}</p>
                        </>
                      ) : (
                        <p>No feedback has been left.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Feedback from Driver */}
                <div className="col-12 col-lg-6 mb-4">
                  <div className="card">
                    <div className="card-body">
                      <h6>
                        Feedback from Driver&nbsp;
                        {pickup.driver && (
                          <Link to={`/drivers/${pickup.driver.id}`}>
                            <b>
                              {pickup.driver.id} {pickup.driver.name}
                            </b>
                          </Link>
                        )}
                      </h6>
                      {pickup.feedbacks.driver &&
                      pickup.feedbacks.driver.rating_by === 2 ? (
                        <>
                          <p>
                            Rating:{' '}
                            {renderStars(
                              parseFloat(pickup.feedbacks.driver.rate) || 0,
                            )}
                          </p>
                          <p>Feedback: {pickup.feedbacks.driver.feedback}</p>
                        </>
                      ) : (
                        <p>No feedback has been left.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '80vh' }}
        >
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}

      <style jsx>{`
        .table td {
          vertical-align: middle;
        }
        .duplicate-pickup-wrap {
          position: absolute;
          top: 50%;
          left: 100%;
          transform: translate(-100%, -55%);
        }
        @media only screen and (max-width: 768px) {
          .duplicate-pickup-wrap {
            position: static;
            transform: translateX(0);
          }
          .duplicate-pickup-btn {
            padding: 0.25rem 0.5rem;
            font-size: 0.875rem;
            line-height: 1.5;
            border-radius: 0.2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default withAuth(PickupDetails);
